import { createInertiaApp } from '@inertiajs/react'
import { createElement, Fragment, type ReactNode } from 'react'

import { ThemeProvider } from '@/components/theme-provider'
import { Toaster } from '@/components/ui/toaster'
import RootLayout from '@/layouts/root-layout'
import { formatPageTitle } from '@/lib/page-title'

// Recover from deploy skew: stale tabs may request hashed chunks that the new
// release no longer serves. Vite emits this when a dynamic import fails.
const PRELOAD_RELOAD_AT = 'vite:preload-reload-at'
window.addEventListener('vite:preloadError', (event) => {
  event.preventDefault()

  const lastReloadAt = Number(sessionStorage.getItem(PRELOAD_RELOAD_AT) ?? '0')
  if (Date.now() - lastReloadAt < 10_000) {
    return
  }

  sessionStorage.setItem(PRELOAD_RELOAD_AT, String(Date.now()))
  window.location.reload()
})

void createInertiaApp({
  title: formatPageTitle,

  // Eager pages: only a handful of screens, and avoids per-page dynamic chunks
  // that 404 mid-deploy when Kamal swaps the public asset volume.
  pages: {
    path: '../pages',
    lazy: false,
  },

  // FlashToasts uses usePage — must live inside App, not as a withApp sibling.
  layout: () => RootLayout,

  strictMode: true,

  withApp(app) {
    return createElement(
      ThemeProvider,
      null,
      createElement(Fragment, null, app as ReactNode, createElement(Toaster)),
    )
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
      withAllErrors: true,
    },
    visitOptions: () => {
      return { queryStringArrayFormat: 'brackets' }
    },
  },
}).catch((error) => {
  if (document.getElementById('app')) {
    throw error
  }

  console.error(
    'Missing root element.\n\n' +
      'If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n' +
      'Consider moving <%= vite_typescript_tag "inertia.tsx" %> to the Inertia-specific layout instead.',
  )
})
